import React, {FC, useState} from "react";
import {T, useTranslate} from "@tolgee/react";
import Button from "@material-ui/core/Button";
import CssBaseline from "@material-ui/core/CssBaseline";
import {Box, CircularProgress, Paper, Typography} from "@material-ui/core";
import {red} from "@material-ui/core/colors";
import CancelIcon from "@material-ui/icons/Cancel";
import {Link} from "react-router-dom";
import {useForm} from "react-hook-form";
import Copyright from "view/components/Copyright";
import FlexBox from "view/components/Layout/FlexBox";
import GoogleSignInButton from "view/components/GoogleSignInButton";
import PublicLanguagePicker from "view/components/LanguagePicker/PublicLanguagePicker";
import {FFairPrimaryIconBlueLogo} from "view/images/FFairLogo";
import Form, {Actions, BasicInput} from "view/components/Form";
import Alert from "view/components/Alert";
import BackdropLoader from "view/components/BackdropLoader";
import PasswordInputs from "view/components/PasswordInputs";
import useStyles from "./style";

type Props = {
  /** Called with the entered password on submit. Should throw on failure. */
  onSubmit: (password: string) => Promise<void>;
  email?: string;
  showName?: string;
  contactEmail?: string;
  isLoading?: boolean;
  showLanguagePicker?: boolean;
  /** Omitted where accepting the invite with Google does not apply. */
  onGoogleSignUp?: () => void;
  isGoogleBusy?: boolean;
  /** Rendered below the three fields — a refused Google account, usually. */
  googleError?: React.ReactNode;
  /**
   * The invite can no longer be accepted — expired, already used, or naming an
   * account that is gone. The card says so instead of offering the form.
   */
  isInviteInvalid?: boolean;
};

type FormData = {
  email: string;
  password: string;
  confirmPassword: string;
};

const SignUpPresentation: FC<Props> = ({
  onSubmit,
  email,
  showName,
  contactEmail,
  isLoading = false,
  showLanguagePicker = false,
  onGoogleSignUp,
  isGoogleBusy = false,
  googleError,
  isInviteInvalid = false,
}) => {
  const classes = useStyles();
  const {t} = useTranslate();
  const [error, setError] = useState<string | null>(null);
  const form = useForm<FormData>({
    mode: "all",
    defaultValues: {
      email: email ?? "",
      password: "",
      confirmPassword: "",
    },
  });

  const {
    handleSubmit,
    formState: {isSubmitting, isDirty, isValid},
  } = form;

  const isDisabled = !isDirty || !isValid || isSubmitting;
  const isShowDataLoaded = showName && email && contactEmail;

  const handleSubmitForm = async (data: FormData) => {
    try {
      setError(null);
      await onSubmit(data.password);
    } catch (e: any) {
      const status = e?.response?.status;
      if (status === 401) {
        setError(t("global.sign_up.error.invite_expired"));
      } else if (status === 404) {
        setError(t("global.sign_up.error.user_not_found"));
      } else if (status === 400) {
        setError(t("global.sign_up.error.password_requirements"));
      } else {
        setError(t("global.sign_up.error.generic"));
      }
    }
  };

  const renderAlert = () => {
    if (error) {
      return (
        <Alert severity="error" className={classes.alert}>
          {error}
        </Alert>
      );
    }
    if (!contactEmail) return null;
    if (isShowDataLoaded) {
      return (
        <Alert severity="info" className={classes.alert}>
          <T
            keyName="global.sign_up.email_added_info"
            params={{
              info: <span className={classes.information} />,
              email,
              showName,
              contactEmail,
            }}
          />
        </Alert>
      );
    }
    return (
      <Alert severity="info" className={classes.alert}>
        <T
          keyName="global.sign_up.page_for_info"
          params={{
            info: <span className={classes.information} />,
            contactEmail,
          }}
        />
      </Alert>
    );
  };

  const renderInvalidInvite = () => (
    <Box className={classes.invalidInvite}>
      <FlexBox gap={1} alignCenter justifyCenter>
        <CancelIcon htmlColor={red[500]} />
        <Typography variant="h4" color="error">
          <T keyName="global.sign_up.invite_link_invalid" />
        </Typography>
      </FlexBox>
      <Typography variant="body2">
        <T keyName="global.sign_up.invite_link_invalid_hint" />
      </Typography>
      <Button
        variant="contained"
        color="primary"
        className={classes.invalidInviteClose}
        onClick={() => window.close()}
      >
        <T keyName="common.action.close" />
      </Button>
    </Box>
  );

  const renderSignUp = () => (
    <>
      <Typography variant="body2" className={classes.helperText}>
        <T keyName="global.sign_up.create_password_subtitle" />
      </Typography>
      {onGoogleSignUp && (
        <Box className={classes.googleButton}>
          <GoogleSignInButton
            onClick={onGoogleSignUp}
            labelKey="global.sign_up.sign_up_with_google"
            isLoading={isGoogleBusy}
          />
        </Box>
      )}
      {renderAlert()}
      <Form
        form={form}
        onSubmit={handleSubmit(handleSubmitForm)}
        className={classes.form}
      >
        <FlexBox column gap={2} fullWidth>
          {email && (
            <BasicInput
              name="email"
              label={t("common.field.email")}
              id="email"
              disabled
              type="text"
              inputProps={{readOnly: true, disabled: true}}
              style={{marginBottom: 0}}
            />
          )}
          <PasswordInputs t={t} gap={2} />
        </FlexBox>
        {googleError && (
          <Alert severity="error" className={classes.googleAlert}>
            {googleError}
          </Alert>
        )}
        <Actions className={classes.actions}>
          <Link to="/login" className={classes.signInLink}>
            <T keyName="global.sign_up.already_have_account_link" />
          </Link>
          <Button
            type="submit"
            fullWidth
            variant="contained"
            color="primary"
            endIcon={
              isSubmitting && <CircularProgress size={18} color="inherit" />
            }
            disabled={isDisabled}
            className={classes.submit}
            classes={{
              disabled: classes.submitDisabled,
            }}
          >
            <T keyName="global.sign_up.sign_up_caps" />
          </Button>
        </Actions>
      </Form>
    </>
  );

  return (
    <>
      <Box className={classes.content}>
        <Box className={classes.container}>
          <Paper elevation={0} className={classes.paper}>
            <BackdropLoader isLoading={isLoading} />
            <CssBaseline />
            <FFairPrimaryIconBlueLogo width={64} height={64} />
            <Typography className={classes.title}>
              <T keyName="global.sign_up.welcome" />
            </Typography>
            {isInviteInvalid ? renderInvalidInvite() : renderSignUp()}
          </Paper>
        </Box>
        {showLanguagePicker && (
          <Box>
            <PublicLanguagePicker />
          </Box>
        )}
        <Box>
          <Copyright />
        </Box>
      </Box>
    </>
  );
};

export default SignUpPresentation;
